import Phaser from 'phaser';
import {
  HAIRSTYLES,
  HAIR_COLORS,
  EYE_COLORS,
  SKIN_TONES,
  HELMETS,
  HELMET_LABEL,
  OUTFIT_PATTERNS,
  OUTFIT_PATTERN_LABEL,
  OUTFIT_COLORS,
  HAIRSTYLE_LABEL,
  DEFAULT_CHARACTER,
} from '../config/presets';
import type { CharacterConfig, Difficulty, HairstyleKey, HelmetKey, OutfitPatternKey } from '../types';
import { makeRider } from '../render/character';
import { GameState } from '../state/GameState';

export class CharacterCreatorScene extends Phaser.Scene {
  private cfg: CharacterConfig = { ...DEFAULT_CHARACTER };
  private preview!: Phaser.GameObjects.Container;
  private difficulty: Difficulty = 'medium';

  constructor() {
    super('CharacterCreatorScene');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.add.rectangle(0, 0, W, H, 0xfceabb).setOrigin(0, 0);
    this.add
      .text(W / 2, 40, 'Create Your Rider', {
        fontFamily: 'sans-serif',
        fontSize: '40px',
        color: '#3d2914',
      })
      .setOrigin(0.5);

    // Preview pane on the right
    this.add.rectangle(W - 260, H / 2, 360, H - 140, 0xffffff, 0.7).setStrokeStyle(2, 0x3d2914);
    this.preview = this.add.container(W - 260, H / 2);
    this.redrawPreview();

    // Picker rows
    let y = 110;
    const rowGap = 64;
    this.makeRow(40, y, 'Hairstyle', HAIRSTYLES, this.cfg.hairstyle, (v) => {
      this.cfg.hairstyle = v as HairstyleKey;
      this.redrawPreview();
    }, (v) => HAIRSTYLE_LABEL[v as HairstyleKey]);
    y += rowGap;
    this.makeColorRow(40, y, 'Hair Color', HAIR_COLORS, this.cfg.hairColor, (v) => {
      this.cfg.hairColor = v;
      this.redrawPreview();
    });
    y += rowGap;
    this.makeColorRow(40, y, 'Eye Color', EYE_COLORS, this.cfg.eyeColor, (v) => {
      this.cfg.eyeColor = v;
      this.redrawPreview();
    });
    y += rowGap;
    this.makeColorRow(40, y, 'Skin Tone', SKIN_TONES, this.cfg.skinTone, (v) => {
      this.cfg.skinTone = v;
      this.redrawPreview();
    });
    y += rowGap;
    this.makeRow(40, y, 'Helmet', HELMETS, this.cfg.helmet, (v) => {
      this.cfg.helmet = v as HelmetKey;
      this.redrawPreview();
    }, (v) => HELMET_LABEL[v as HelmetKey]);
    y += rowGap;
    this.makeRow(40, y, 'Outfit Pattern', OUTFIT_PATTERNS, this.cfg.outfitPattern, (v) => {
      this.cfg.outfitPattern = v as OutfitPatternKey;
      this.redrawPreview();
    }, (v) => OUTFIT_PATTERN_LABEL[v as OutfitPatternKey]);
    y += rowGap;
    this.makeColorRow(40, y, 'Shirt Color', OUTFIT_COLORS, this.cfg.outfitPrimary, (v) => {
      this.cfg.outfitPrimary = v;
      this.redrawPreview();
    });
    y += rowGap;
    this.makeColorRow(40, y, 'Pants/Accent', OUTFIT_COLORS, this.cfg.outfitSecondary, (v) => {
      this.cfg.outfitSecondary = v;
      this.redrawPreview();
    });
    y += 64;

    // Difficulty picker
    this.makeDifficultyRow(40, y);

    // Back button (left)
    const back = this.add.container(W / 2 - 140, H - 50);
    const backBg = this.add.rectangle(0, 0, 130, 50, 0x6b3410).setStrokeStyle(2, 0xffffff);
    const backTxt = this.add
      .text(0, 0, '← Back', { fontFamily: 'sans-serif', fontSize: '20px', color: '#fff' })
      .setOrigin(0.5);
    back.add([backBg, backTxt]);
    back.setSize(130, 50).setInteractive({ useHandCursor: true });
    back.on('pointerover', () => backBg.setFillStyle(0x8a4a1a));
    back.on('pointerout', () => backBg.setFillStyle(0x6b3410));
    back.on('pointerup', () => this.scene.start('TitleScene'));

    // Next button (right)
    const next = this.add.container(W / 2 + 80, H - 50);
    const bg = this.add.rectangle(0, 0, 220, 50, 0x3d2914).setStrokeStyle(2, 0xffffff);
    const txt = this.add
      .text(0, 0, 'Next: Pick Animal', { fontFamily: 'sans-serif', fontSize: '22px', color: '#fff' })
      .setOrigin(0.5);
    next.add([bg, txt]);
    next.setSize(220, 50).setInteractive({ useHandCursor: true });
    next.on('pointerover', () => bg.setFillStyle(0x6b3410));
    next.on('pointerout', () => bg.setFillStyle(0x3d2914));
    next.on('pointerup', () => {
      GameState.setCharacter(this.registry, this.cfg);
      GameState.setDifficulty(this.registry, this.difficulty);
      this.scene.start('AnimalSelectorScene');
    });
  }

  private makeDifficultyRow(x: number, y: number): void {
    this.add.text(x, y, 'Difficulty', {
      fontFamily: 'sans-serif',
      fontSize: '20px',
      color: '#3d2914',
    });
    const labels: { key: Difficulty; label: string; color: number }[] = [
      { key: 'easy', label: 'Easy', color: 0x4ea36b },
      { key: 'medium', label: 'Medium', color: 0xeac98a },
      { key: 'hard', label: 'Hard', color: 0xc23b3b },
    ];
    const buttons: Partial<Record<Difficulty, Phaser.GameObjects.Rectangle>> = {};
    labels.forEach((l, i) => {
      const bx = x + 200 + i * 110;
      const bg = this.add
        .rectangle(bx, y + 12, 90, 32, l.color)
        .setStrokeStyle(this.difficulty === l.key ? 4 : 2, 0x3d2914)
        .setInteractive({ useHandCursor: true });
      this.add
        .text(bx, y + 12, l.label, {
          fontFamily: 'sans-serif',
          fontSize: '16px',
          color: '#3d2914',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        this.difficulty = l.key;
        Object.values(buttons).forEach((b) => b?.setStrokeStyle(2, 0x3d2914));
        bg.setStrokeStyle(4, 0x3d2914);
      });
      buttons[l.key] = bg;
    });
  }

  private redrawPreview(): void {
    this.preview.removeAll(true);
    const rider = makeRider(this, this.cfg);
    rider.setScale(4);
    rider.y = 80;
    this.preview.add(rider);
  }

  private makeRow<T extends string>(
    x: number,
    y: number,
    label: string,
    options: T[],
    initial: T,
    onChange: (v: T) => void,
    labelFor: (v: T) => string,
  ): void {
    this.add.text(x, y, label, {
      fontFamily: 'sans-serif',
      fontSize: '20px',
      color: '#3d2914',
    });
    let idx = options.indexOf(initial);
    const valueText = this.add.text(x + 200, y, labelFor(options[idx]), {
      fontFamily: 'sans-serif',
      fontSize: '20px',
      color: '#222',
      backgroundColor: '#fff8e0',
      padding: { left: 10, right: 10, top: 4, bottom: 4 },
    });
    valueText.setFixedSize(220, 32);
    const update = () => {
      valueText.setText(labelFor(options[idx]));
      onChange(options[idx]);
    };
    this.makeArrow(x + 170, y + 10, '◀', () => {
      idx = (idx - 1 + options.length) % options.length;
      update();
    });
    this.makeArrow(x + 430, y + 10, '▶', () => {
      idx = (idx + 1) % options.length;
      update();
    });
  }

  private makeColorRow(
    x: number,
    y: number,
    label: string,
    options: number[],
    initial: number,
    onChange: (v: number) => void,
  ): void {
    this.add.text(x, y, label, {
      fontFamily: 'sans-serif',
      fontSize: '20px',
      color: '#3d2914',
    });
    const swatches: Phaser.GameObjects.Rectangle[] = [];
    options.forEach((c, i) => {
      const sx = x + 200 + i * 36;
      const swatch = this.add
        .rectangle(sx, y + 12, 28, 28, c)
        .setStrokeStyle(c === initial ? 4 : 1, 0x3d2914)
        .setInteractive({ useHandCursor: true });
      swatch.on('pointerdown', () => {
        swatches.forEach((s) => s.setStrokeStyle(1, 0x3d2914));
        swatch.setStrokeStyle(4, 0x3d2914);
        onChange(c);
      });
      swatches.push(swatch);
    });
  }

  private makeArrow(x: number, y: number, glyph: string, onClick: () => void): void {
    const t = this.add
      .text(x, y, glyph, {
        fontFamily: 'sans-serif',
        fontSize: '24px',
        color: '#3d2914',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerdown', onClick);
  }
}
